/**
 * Fractional Indexing (SPEC 5.1).
 *
 * `position` ist kein Integer, sondern ein lexikografisch sortierbarer String.
 * Grund: Beim Verschieben einer Karte schreibt der Server genau EINE Zeile.
 * Integer-Positionen wuerden bei parallel verschiebenden Nutzern zu
 * Reindex-Stuermen und Race Conditions fuehren.
 */
import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';
/** Position zwischen zwei Nachbarn. `null` bedeutet Anfang bzw. Ende der Liste. */
export function positionBetween(prev, next) {
    return generateKeyBetween(prev ?? null, next ?? null);
}
/** Position am Ende einer Liste — der haeufigste Fall beim Anlegen. */
export function positionAtEnd(last) {
    return generateKeyBetween(last ?? null, null);
}
/** Position am Anfang einer Liste. */
export function positionAtStart(first) {
    return generateKeyBetween(null, first ?? null);
}
/** `count` gleichmaessig verteilte Positionen zwischen zwei Nachbarn. */
export function positionsBetween(prev, next, count) {
    if (count < 0)
        throw new RangeError('count darf nicht negativ sein');
    return generateNKeysBetween(prev ?? null, next ?? null, count);
}
/**
 * Position fuer eine Einfuegung an Index `index` in eine bereits sortierte
 * Liste bestehender Positionen.
 */
export function positionForInsert(sortedPositions, index) {
    const clamped = Math.max(0, Math.min(index, sortedPositions.length));
    const prev = clamped > 0 ? (sortedPositions[clamped - 1] ?? null) : null;
    const next = clamped < sortedPositions.length ? (sortedPositions[clamped] ?? null) : null;
    return positionBetween(prev, next);
}
/** Streng aufsteigend sortiert? Doppelte Keys gelten als ungueltig. */
export function isStrictlyOrdered(positions) {
    for (let i = 1; i < positions.length; i += 1) {
        const prev = positions[i - 1];
        const current = positions[i];
        if (prev === undefined || current === undefined)
            return false;
        if (prev >= current)
            return false;
    }
    return true;
}
/**
 * Kollision: zwei Objekte derselben Spalte tragen denselben Key. Passiert, wenn
 * zwei Nutzer gleichzeitig an dieselbe Stelle einfuegen. Loesung ist kein
 * Fehler, sondern ein transaktionaler Rebalance der betroffenen Spalte.
 */
export function needsRebalance(positions) {
    return new Set(positions).size !== positions.length;
}
/**
 * Erzeugt fuer `count` Objekte einen frischen, gleichmaessig verteilten
 * Satz Positionen. Der Aufrufer schreibt sie in der bestehenden Reihenfolge
 * zurueck — transaktional, sonst entsteht ein inkonsistenter Zwischenstand.
 */
export function rebalancedPositions(count) {
    return positionsBetween(null, null, count);
}
/** Vergleichsfunktion fuer `Array.prototype.sort`. */
export function byPosition(a, b) {
    if (a.position < b.position)
        return -1;
    if (a.position > b.position)
        return 1;
    return 0;
}
//# sourceMappingURL=position.js.map